import React, {Fragment, useEffect, useState} from 'react';
import b_ from 'b_';
import {logError} from '@yandex-int/pythia-libs';

import {SurveysGoal, SurveySlug, SurveysParam, SurveyPreset} from 'client/common/types';

import {useModal} from 'client/common/hooks/modal';
import {useIsAuthorized, useUserCountryId} from 'client/common/hooks/user';
import {openLink} from 'client/common/utils/window';
import {sendGoal} from 'client/common/utils/metrika';

import {ClientSelectModal} from 'client/common/components/client-select';

import SurveyPresets from 'client/quicksurvey/survey-create/components/survey-presets';
import PresetDescription from 'client/quicksurvey/survey-create/components/preset-description';

import {isSimpleTemplate} from 'client/common/store/reducers/question-bank/types';

import I18N from 'assets/i18n/brandlift-project';

import {getSurveyCreateRequestParams, LINK_BY_PRESET, NEW_WINDOW_LINKS} from './consts';
import {SurveyCreatorProps} from './types';

import './survey-creator.css';
import {useCreateSurvey} from 'client/common/hooks/survey';

const i18n = I18N.surveyCreate.surveyCreator;
const b = b_.with('survey-creator');

const SUBMIT_LABEL = {
    [SurveyPreset.Creative]: i18n.submitLabel.toTests,
    [SurveyPreset.Bordomatik]: i18n.submitLabel.toTests,
    [SurveyPreset.BannerTest]: i18n.submitLabel.toTests,
    [SurveyPreset.CreateForMe]: i18n.submitLabel.createForMe,
    [SurveyPreset.Crowd]: i18n.submitLabel.crowd
} as Partial<Record<SurveyPreset, string>>;

function useSeenPresetCount(preset?: SurveyPreset): number {
    const [seenPresets, setSeenPresets] = useState(new Set());

    useEffect(() => {
        if (preset) {
            setSeenPresets(presets => presets.add(preset));
        }
    }, [preset]);

    return seenPresets.size;
}

const openCreatedSurvey = (preset: SurveyPreset, slug: SurveySlug) => {
    if (preset === SurveyPreset.Simple || isSimpleTemplate(preset)) {
        return openLink(`/qs/${slug}`);
    }

    if (preset === SurveyPreset.UX) {
        return openLink(`/ux/${slug}`);
    }

    return openLink(`/qs/${slug}`);
};

// eslint-disable-next-line max-statements
export default function SurveyCreator(props: SurveyCreatorProps) {
    const {initialPreset, preset, selectPreset, sendParams, userIsAgency} = props;

    const [showModal, hideModal] = useModal();

    const [selectingStartTime] = useState(performance.now());
    const isAuthorized = useIsAuthorized();

    useEffect(() => {
        if (!preset && initialPreset) {
            selectPreset({preset: initialPreset});
        }
    }, [preset, initialPreset, selectPreset]);

    const seenPresetCount = useSeenPresetCount(preset);
    const countryId = useUserCountryId() ?? 225;

    const handlePresetClick = (name: SurveyPreset) => {
        selectPreset({preset: name});

        sendParams([SurveysParam.SelectedCategory, name]);
        sendGoal(SurveysGoal.ClickSurveyPreset);
    };

    const createSurvey = useCreateSurvey();

    const createSurveyWithSelectedPreset = async (
        selectedPreset: SurveyPreset,
        clientId?: string,
        accountId?: any
    ) => {
        const params = getSurveyCreateRequestParams({
            preset: selectedPreset,
            clientId,
            isAuthorized,
            accountId,
            countryId
        });

        const createSurveyResponse = await createSurvey({survey: params});
        const newSurveySlug = createSurveyResponse?.data.slug;

        if (newSurveySlug) {
            openCreatedSurvey(selectedPreset, newSurveySlug);
        } else {
            logError({
                message: 'Failed to create survey with params',
                additional: {
                    ...params,
                    response: createSurveyResponse
                }
            });
        }
    };

    const handleMoveToEditing = ({
        preset: selectedPreset,
        accountId
    }: {
        preset: SurveyPreset;
        accountId?: number;
    }) => {
        if (userIsAgency) {
            const handleClientSelect = async (clientId: string) => {
                hideModal();

                await createSurveyWithSelectedPreset(selectedPreset, clientId, accountId);
            };

            showModal(<ClientSelectModal onFinish={handleClientSelect} />);
        } else {
            return createSurveyWithSelectedPreset(selectedPreset);
        }
    };

    // eslint-disable-next-line max-statements
    const handleSubmit = ({
        preset: selectedPreset,
        accountId
    }: {
        preset: SurveyPreset;
        accountId?: number;
    }) => {
        const selectingEndTime = performance.now();
        const selectingTime = Math.round((selectingEndTime - selectingStartTime) / 1000);

        sendGoal(SurveysGoal.ConstructorFromCategory);
        sendParams([SurveysParam.QuestionSelectingTime, selectingTime]);
        sendParams([SurveysParam.SeenCategoryCount, seenPresetCount]);
        sendParams([SurveysParam.ConstructorFromCategory, selectedPreset]);

        if (Object.keys(LINK_BY_PRESET).includes(selectedPreset)) {
            const linkedPreset = selectedPreset as keyof typeof LINK_BY_PRESET;
            const newWindow = NEW_WINDOW_LINKS.includes(linkedPreset);

            openLink(LINK_BY_PRESET[linkedPreset], newWindow);

            return !newWindow;
        }

        void handleMoveToEditing({preset: selectedPreset, accountId});

        return true;
    };

    return (
        <div className={b()}>
            <SurveyPresets onPresetClick={handlePresetClick} />
            {/* key=preset для анимации при смене категории */}
            <Fragment key={preset}>
                <PresetDescription
                    onSubmit={handleSubmit}
                    submitLabel={SUBMIT_LABEL[preset as SurveyPreset]}
                />
            </Fragment>
        </div>
    );
}
